import {
  useMutationSmartCollectionCreate,
  type SmartCollectionCreateInput,
} from '@fabric-app/business/smart-collections/mutations/useMutationSmartCollectionCreate';
import { toast } from '@fabric-app/business/store/alerts';
import { IconDropdown14SmartCollection } from '@fabric-app/icons/svg';
import type { QueryResourceListContextSearchParams_Filters } from '@fabric-app/types/resources';
import { FloatingFooter } from '@fabric-app/ui/components/FloatingFooter';
import type { Kind } from '@fabric/woody-client';
import equal from 'fast-deep-equal';
import { AnimatePresence } from 'framer-motion';
import { useRouter } from '@fabric-app/compat';
import type React from 'react';
import { useMemo, useState } from 'react';
import { generatePath } from '@fabric-app/utils/url/routing';
import { appRoutes } from '@fabric-app/business/app/routing/routes.config';
import { useLabsFeatureEnabled } from '@fabric-app/business/labs/hooks/useLabsFeatureEnabled';
import { LabFeature } from '@fabric-app/types/labs';

export interface FloatingFooterSaveAsSmartCollectionProps {
  enabled: boolean;
  filters: QueryResourceListContextSearchParams_Filters;
  text: string;
}

export const FloatingFooterSaveAsSmartCollection: React.FC<
  FloatingFooterSaveAsSmartCollectionProps
> = ({ enabled, filters, text }) => {
  const { mutate, isPending } = useMutationSmartCollectionCreate();

  const query: Exclude<SmartCollectionCreateInput['queries'], undefined>[number] = useMemo(
    () => ({
      filters: {
        tags: filters.tagIds,
        kinds: filters.kinds as Kind[],
        color: filters.color,
      },
      text,
      /**
       * the TS doesn't like the mode: 'hybrid'
       * but importing the QueryMode enum breaks the build...
       */
      mode: 'hybrid' as any,
    }),
    [filters, text],
  );

  const [lastSavedQuery, setLastSavedQuery] = useState<typeof query | null>(null);

  const isCurrentQuerySaved = useMemo(() => {
    return equal(lastSavedQuery, query);
  }, [lastSavedQuery, query]);

  const router = useRouter();

  const isFeatureEnabled = useLabsFeatureEnabled(LabFeature.SMART_COLLECTIONS);

  return (
    <AnimatePresence>
      {enabled && !isCurrentQuerySaved && isFeatureEnabled && (
        <FloatingFooter.Container alwaysFloating layoutId="save-as-smart-collection">
          <FloatingFooter.Button
            variant="bg-secondary"
            isLoading={isPending}
            onClick={() => {
              mutate(
                {
                  data: {
                    queries: [query],
                  },
                },
                {
                  onSuccess: (data) => {
                    setLastSavedQuery(query);
                    toast({
                      content: 'Search saved as smart collection',
                    });
                    router.push(
                      generatePath(appRoutes.smartCollections.detail, {
                        smartCollectionId: data.id,
                      }),
                    );
                  },
                },
              );
            }}
          >
            <IconDropdown14SmartCollection />
            Save search as smart collection
          </FloatingFooter.Button>
        </FloatingFooter.Container>
      )}
    </AnimatePresence>
  );
};
